import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Cookie, Settings as SettingsIcon, X } from 'lucide-react';
import { Link } from 'react-router-dom';

const BACKEND_URL = '';
const STORAGE_KEY = 'sll_cookie_consent_v2';

// Cookie categories. Necessary is always on. Members can toggle the rest.
const COOKIE_CATEGORIES = [
  { key: 'necessary', label: 'Strictly necessary', description: 'Essential for the site to function (e.g. session, authentication).', required: true },
  { key: 'preferences', label: 'Preferences', description: 'Remembers things like your theme and language choices.', required: false },
  { key: 'analytics', label: 'Analytics', description: 'Helps us understand how the platform is used so we can improve it.', required: false },
  { key: 'marketing', label: 'Marketing', description: 'Used to show relevant updates and partner content.', required: false },
];

function loadStored() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function persist(state) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...state, ts: new Date().toISOString() }));
  } catch {}
  // Best-effort sync to server — fires-and-forgets so it never blocks UX.
  fetch(`${BACKEND_URL}/api/consent/cookies`, {
    method: 'POST', credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(state),
  }).catch(() => {});
}

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [managing, setManaging] = useState(false);
  const [prefs, setPrefs] = useState({ necessary: true, preferences: true, analytics: true, marketing: false });

  useEffect(() => {
    if (!loadStored()) setVisible(true);
  }, []);

  if (!visible) return null;

  const handleAcceptAll = () => {
    const all = { necessary: true, preferences: true, analytics: true, marketing: true };
    persist({ choice: 'accept_all', preferences: all });
    setVisible(false);
  };
  const handleRejectAll = () => {
    const none = { necessary: true, preferences: false, analytics: false, marketing: false };
    persist({ choice: 'reject_all', preferences: none });
    setVisible(false);
  };
  const handleSaveCustom = () => {
    persist({ choice: 'custom', preferences: prefs });
    setVisible(false);
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[60] p-3 md:p-4 pointer-events-none" data-testid="cookie-consent-banner">
      <div className="max-w-4xl mx-auto pointer-events-auto">
        <div className="bg-white border border-slate-200 rounded-2xl shadow-2xl overflow-hidden">
          {!managing ? (
            <div className="p-4 md:p-5 flex flex-col md:flex-row items-start md:items-center gap-3 md:gap-4">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <Cookie className="h-5 w-5 text-amber-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-800">We value your privacy</p>
                  <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">
                    We use cookies to provide essential features and improve your experience.
                    See our <Link to="/governance/privacy-policy" className="text-brand-navy underline hover:text-brand-dark">Privacy Policy</Link>.
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap w-full md:w-auto">
                <Button variant="outline" size="sm" onClick={() => setManaging(true)} className="text-xs" data-testid="cookie-manage-btn">
                  <SettingsIcon className="h-3 w-3 mr-1" /> Manage Preferences
                </Button>
                <Button variant="outline" size="sm" onClick={handleRejectAll} className="text-xs" data-testid="cookie-reject-btn">
                  Reject
                </Button>
                <Button size="sm" onClick={handleAcceptAll} className="text-xs bg-brand-navy text-white hover:bg-brand-dark" data-testid="cookie-accept-btn">
                  Accept
                </Button>
              </div>
            </div>
          ) : (
            <div className="p-4 md:p-5" data-testid="cookie-manage-panel">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-sm font-semibold text-slate-800">Cookie Preferences</p>
                  <p className="text-xs text-slate-500">Choose which cookie categories you allow.</p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setManaging(false)} className="h-8 w-8 p-0">
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <div className="space-y-2 mb-3">
                {COOKIE_CATEGORIES.map(cat => (
                  <label key={cat.key} className="flex items-start gap-3 p-2.5 rounded-lg border border-slate-200 cursor-pointer hover:bg-slate-50" data-testid={`cookie-cat-${cat.key}`}>
                    <Checkbox
                      checked={cat.required ? true : prefs[cat.key]}
                      disabled={cat.required}
                      onCheckedChange={(v) => setPrefs(p => ({ ...p, [cat.key]: !!v }))}
                      className="mt-0.5"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-slate-700">
                        {cat.label}
                        {cat.required && <span className="ml-2 text-[10px] text-slate-400 uppercase">Always on</span>}
                      </p>
                      <p className="text-[11px] text-slate-500">{cat.description}</p>
                    </div>
                  </label>
                ))}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Button variant="outline" size="sm" onClick={handleRejectAll} className="text-xs">Reject all</Button>
                <Button variant="outline" size="sm" onClick={handleAcceptAll} className="text-xs">Accept all</Button>
                <Button size="sm" onClick={handleSaveCustom} className="text-xs bg-brand-navy text-white hover:bg-brand-dark ml-auto" data-testid="cookie-save-custom-btn">
                  Save preferences
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
